/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {Uuid} from '../../components/util/uuid';
import {LocalBulkCreativeInfo} from '../../components/session/local-bulk-creative-info';
import {MediaTypeHelper} from '../../components/util/media-type-helper';
import {advertiserFromNavigation, creativeSetFromNavigation, lineItemFromNavigation} from '../../components/util/navigation-context';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {CreativeBulkActionService} from './creative-bulk-action.service';
import {CreativeAttachModalService} from './creative-attach-modal.service';
import {CreativeCopyInBulkModalService} from './creative-copy-in-bulk-modal.service';
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {bulkApiErrorsFromResponse} from '../common/controller/bulk-action-errors';
import {FormSubmitState} from '../common/controller/form-submit-state';
import {setFormValidity} from '../common/controller/form-validity';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';

export interface CreativeBulkUploadSetupParams {
  creativeType: string;
  navigationHistory: NavigationHistoryService;
  routeParams: any;
  Asset: any;
  Layout: any;
  Team: any;
  LineItem: any;
  LocalNetworkProfile: any;
  LibraryCreative: any;
  Creative: any;
  adnListHelper: AdnListHelperService;
  LocalUserPermissions: any;
  creativeBulkActionService: CreativeBulkActionService;
  creativeAttachModalService: CreativeAttachModalService;
  creativeCopyInBulkModalService: CreativeCopyInBulkModalService;
  cdr: any;
}

export function buildCreativeBulkSavePlan(uiCreatives: any[], advertiser?: any): any {
  const bulkSaveCreatives = [],
    copyCreatives = [],
    allCreatives = [];
  _.forEach(uiCreatives, function(c) {
    const goodLineItems = c.lineItemAssignmentType === 'CREATIVE_SET' ? [] : _.uniqBy(_.filter(c.lineItems, function(li) {
      return li.id;
    }), 'id');

    const goodCreativeSets = c.lineItemAssignmentType === 'CREATIVE_SET' ? _.uniqBy(_.filter(c.creativeSets, function(cs) {
      return cs.id;
    }), 'id') : [];

    const basicCreative: any = _.cloneDeep(c);
    basicCreative.id = Uuid.generate();
    delete basicCreative.lineItems;
    delete basicCreative.creativeSets;

    if (goodLineItems.length === 0 && goodCreativeSets.length === 0) {
      const theC = _.cloneDeep(basicCreative);
      if (_.get(advertiser, ['id'])) {
        theC.advertiser = advertiser;
      }
      bulkSaveCreatives.push(theC);
      allCreatives.push(theC);
    }

    _.forEach(goodLineItems, function(li, index) {
      const creative = _.cloneDeep(basicCreative);
      creative.lineItem = li;
      if (index === 0) {
        bulkSaveCreatives.push(creative);
      } else {
        creative.id = Uuid.generate();
        copyCreatives.push({
          copyCreativeId: basicCreative.id,
          liId: li.id,
          id: creative.id
        });
      }
      allCreatives.push(creative);
    });

    _.forEach(goodCreativeSets, function(cs, index) {
      const creative = _.cloneDeep(basicCreative);
      creative.creativeSet = cs;
      creative.id = Uuid.generate();
      if (index === 0) {
        bulkSaveCreatives.push(creative);
      } else {
        copyCreatives.push({
          copyCreativeId: basicCreative.id,
          csId: cs.id,
          id: creative.id
        });
      }
      allCreatives.push(creative);
    });
  });
  return {bulkSaveCreatives, copyCreatives, allCreatives};
}

export function applyHalfSizeDimensions(creative: any, halfSize: boolean): void {
  if (!creative) {
    return;
  }
  if (creative.nativeWidth == null || creative.nativeHeight == null) {
    const currentWidth = Number(creative.width) || 0;
    const currentHeight = Number(creative.height) || 0;
    if (creative.halfSize) {
      creative.nativeWidth = currentWidth * 2;
      creative.nativeHeight = currentHeight * 2;
    } else {
      creative.nativeWidth = currentWidth;
      creative.nativeHeight = currentHeight;
    }
  }
  creative.halfSize = !!halfSize;
  if (halfSize) {
    creative.width = Math.round(Number(creative.nativeWidth) / 2);
    creative.height = Math.round(Number(creative.nativeHeight) / 2);
  } else {
    creative.width = creative.nativeWidth;
    creative.height = creative.nativeHeight;
  }
}

export function copyHalfSizeSetting(source: any, creatives: any[]): void {
  const halfSize = !!source?.halfSize;
  _.forEach(creatives, function(c) {
    applyHalfSizeDimensions(c, halfSize);
  });
}

export function applyCreativeBulkUploadValidationErrors(state: FormSubmitState, response: any): void {
  state.apiErrors = bulkApiErrorsFromResponse(response);
  state.bulkError = true;
}

export function setupCreativeBulkUploadCtrl(ctrl: any, p: CreativeBulkUploadSetupParams): void {
  const {
        navigationHistory,
    routeParams,
    creativeType,
    Asset,
    Layout,
    Team,
    LineItem,
    LocalNetworkProfile,
    LibraryCreative,
    Creative,
    adnListHelper,
    LocalUserPermissions,
    creativeBulkActionService,
    creativeAttachModalService,
    creativeCopyInBulkModalService,
    cdr
  } = p;

  ctrl.model = {};
      ctrl.bulk = {};

      ctrl.isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
      ctrl.allowsPoliticalAds = LocalNetworkProfile.isPoliticalAdsPlatform();
      ctrl.political = false;
      ctrl.bulkCreatives = creativeType === 'CREATIVE' ? LocalBulkCreativeInfo.obtain().getCreatives() : LocalBulkCreativeInfo.obtain().getLibraryCreatives();
      ctrl.view = ctrl.bulkCreatives.length > 0 ? 'LIST' : 'UPLOAD';
      ctrl.perms = LocalUserPermissions.getAllNetworkPermissions();
      ctrl.type = 'BULK_IMAGE';
      ctrl.creativeType = creativeType;
      ctrl.validTypes = {};
      ctrl.validSubTypes = {};
      ctrl.creativeFields = ['name', {param: 'Layout', label: 'layout'}, {param: 'LineItem', label: 'lineItem'}, {param: '', label: 'URLs'}, {label: 'dimensions', param: 'width'}, {param: '', label: 'info'}, 'updateTime', {param: '', label: 'targeting'}];
      ctrl.hasCreativeSet = LocalNetworkInfo.obtain().getInfo('creativeSetCount') > 0;
      ctrl.attachSuffix = "Line Item" + (ctrl.hasCreativeSet ? " or Creative Set" : "");

      ctrl.lineItemOption = LocalNetworkInfo.obtain().getInfo("lineItemsCount") > 0;
      ctrl.creativeSetOption = LocalNetworkInfo.obtain().getInfo("creativeSetCount") > 0;
      ctrl.defaultAssignmentType = ctrl.lineItemOption ? 'LINE_ITEM' : ctrl.creativeSetOption ? 'CREATIVE_SET' : 'LINE_ITEM';

      if (ctrl.creativeType === 'LIBRARY_CREATIVE') {
        Team.query({minimal: true}).$promise.then(function(page) {
          // undefined → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
          deferMarkForCheck(cdr, function() {
            ctrl.allTeams = page.results;
          });
        });
        ctrl.creativeFields = ['name', {param: 'Layout', label: 'layout'}, {param: '', label: 'URLs'}, {label: 'dimensions', param: 'width'}, 'updateTime', {param: '', label: 'targeting'}];
      }

      ctrl.bulkAction = creativeBulkActionService.createBulkAction(ctrl);
      const CreativeResource = creativeType === 'CREATIVE' ? Creative : LibraryCreative;

      ctrl.clearPreviousUploads = function() {
        ctrl.bulkSelection = {};
        LocalBulkCreativeInfo.obtain().setCreatives([]);
        ctrl.bulkCreatives = LocalBulkCreativeInfo.obtain().getCreatives();
        adnListHelper.setUpList(ctrl, CreativeResource, false, null, {id: _.map(ctrl.bulkCreatives, 'id'), pageSize: 1000});
      };

      ctrl.attach = function() {
        creativeAttachModalService.open(ctrl, CreativeResource, function(updatedCreatives, bulkSelection) {
          _.forEach(updatedCreatives, function(uc) {
            ctrl.bulkCreatives.push(uc);
          });
          adnListHelper.setUpList(ctrl, CreativeResource, false, null, {id: _.map(ctrl.bulkCreatives, 'id'), pageSize: 1000});
          LocalBulkCreativeInfo.obtain().setCreatives(ctrl.bulkCreatives);
          ctrl.bulkCreatives = LocalBulkCreativeInfo.obtain().getCreatives();
          ctrl.bulkSelection = bulkSelection;
        });
      };

      ctrl.copyInBulk = function() {
        creativeCopyInBulkModalService.open(ctrl, CreativeResource, function(updatedCreatives, bulkSelection) {
          _.forEach(updatedCreatives, function(uc) {
            ctrl.bulkCreatives.push(uc);
          });
          adnListHelper.setUpList(ctrl, CreativeResource, false, null, {id: _.map(ctrl.bulkCreatives, 'id'), pageSize: 1000});
          LocalBulkCreativeInfo.obtain().setCreatives(ctrl.bulkCreatives);
          ctrl.bulkCreatives = LocalBulkCreativeInfo.obtain().getCreatives();
          ctrl.bulkSelection = bulkSelection;
        });
      };

      if (ctrl.view === 'LIST') {
        adnListHelper.setUpList(ctrl, CreativeResource, false, null, {id: _.map(ctrl.bulkCreatives, 'id'), pageSize: 1000});
      }

      const advertiser = advertiserFromNavigation(navigationHistory, routeParams);
      if (advertiser) {
        ctrl.advertiserSpecified = true;
        ctrl.advertiser = advertiser;
      }

      const lineItem = lineItemFromNavigation(navigationHistory, routeParams);
      if (lineItem) {
        ctrl.lineItem = lineItem;
        ctrl.lineItemSpecified = true;
      }

      const creativeSet = creativeSetFromNavigation(navigationHistory, routeParams);
      if (creativeSet) {
        ctrl.creativeSetFromParams = creativeSet;
      }

      ctrl.typeSwitch = function() {
        if (ctrl.validSubTypes[ctrl.type].asArray.indexOf(ctrl.selected) < 0) {
          ctrl.selected = ctrl.validSubTypes[ctrl.type].asArray[0];
        }
      };

      const params: any = {excludeInvalid: true, onlyOnAxProducts: ctrl.isPureMarketplace};
      if (ctrl.lineItem) {
        params.lineItemId = _.get(ctrl.lineItem, ['id']);
      }
      Layout.query(params).$promise.then(function(data) {
        function layoutsFilter(layouts, political, fullPage) {
          return _.filter(layouts, function(l) {
            return l.political === political && l.fullPage === fullPage;
          });
        }

        const allThirdPartyLayouts = _.orderBy(_.filter(data.results, function(l) {
          return l.layoutType === 'THIRD_PARTY';
        }), function(l) {
          return _.get(l, ['externalReference'], '').toLowerCase().indexOf('priority') > -1 ? 0 : 1;
        });
        ctrl.thirdPartyLayouts = layoutsFilter(allThirdPartyLayouts, false, false);
        ctrl.politicalThirdPartyLayouts = layoutsFilter(allThirdPartyLayouts, true, false);
        ctrl.highImpactThirdPartyLayouts = layoutsFilter(allThirdPartyLayouts, false, true);
        ctrl.highImpactPoliticalThirdPartyLayouts = layoutsFilter(allThirdPartyLayouts, true, true);

        const allImageLayouts = _.orderBy(_.filter(data.results, function(l) {
          const hasImage = _.find(l.layoutComponents, function(lc) {
            return lc.type === 'ASSET' && _.get(MediaTypeHelper.getMediaType(lc.mimeTypes), ['image']);
          });
          const hasVideo = _.find(l.layoutComponents, function(lc) {
            return lc.type === 'ASSET' && _.get(MediaTypeHelper.getMediaType(lc.mimeTypes), ['video']);
          });
          return hasImage && !hasVideo;
        }), function(l) {
          return _.get(l, ['externalReference'], '').toLowerCase().indexOf('priority') > -1 ? 0 : 1;
        });
        ctrl.imageLayouts = layoutsFilter(allImageLayouts, false, false);
        ctrl.politicalImageLayouts = layoutsFilter(allImageLayouts, true, false);
        ctrl.highImpactImageLayouts = layoutsFilter(allImageLayouts, false, true);
        ctrl.highImpactPoliticalImageLayouts = layoutsFilter(allImageLayouts, true, true);

        const allVideoLayouts = _.orderBy(_.filter(data.results, function(l) {
          return _.find(l.layoutComponents, function(lc) {
            return lc.type === 'ASSET' && _.get(MediaTypeHelper.getMediaType(lc.mimeTypes), ['video']);
          });
        }), function(l) {
          return _.get(l, ['externalReference'], '').toLowerCase().indexOf('priority') > -1 ? 0 : 1;
        });
        ctrl.videoLayouts = layoutsFilter(allVideoLayouts, false, false);
        ctrl.politicalVideoLayouts = layoutsFilter(allVideoLayouts, true, false);
        ctrl.highImpactVideoLayouts = layoutsFilter(allVideoLayouts, false, true);
        ctrl.highImpactPoliticalVideoLayouts = layoutsFilter(allVideoLayouts, true, true);

        const allHtmlLayouts = _.orderBy(_.filter(data.results, function(l) {
          return l.layoutType === 'HTML';
        }), function(l) {
          return _.get(l, ['externalReference'], '').toLowerCase().indexOf('priority') > -1 ? 0 : 1;
        });
        ctrl.htmlLayouts = layoutsFilter(allHtmlLayouts, false, false);
        ctrl.politicalHtmlLayouts = layoutsFilter(allHtmlLayouts, true, false);
        ctrl.highImpactHtmlLayouts = layoutsFilter(allHtmlLayouts, false, true);
        ctrl.highImpactPoliticalHtmlLayouts = layoutsFilter(allHtmlLayouts, true, true);

        ctrl.allowedStandard = {
          BULK_HTML: ctrl.htmlLayouts,
          BULK_IMAGE: ctrl.imageLayouts,
          BULK_VIDEO: ctrl.videoLayouts,
          BULK_THIRD_PARTY: ctrl.thirdPartyLayouts
        };
        ctrl.allowedPolitical = {
          BULK_HTML: ctrl.politicalHtmlLayouts,
          BULK_IMAGE: ctrl.politicalImageLayouts,
          BULK_VIDEO: ctrl.politicalVideoLayouts,
          BULK_THIRD_PARTY: ctrl.politicalThirdPartyLayouts
        };
        ctrl.allowedHighImpact = {
          BULK_HTML: ctrl.highImpactHtmlLayouts,
          BULK_IMAGE: ctrl.highImpactImageLayouts,
          BULK_VIDEO: ctrl.highImpactVideoLayouts,
          BULK_THIRD_PARTY: ctrl.highImpactThirdPartyLayouts
        };
        ctrl.allowedHighImpactPolitical = {
          BULK_HTML: ctrl.highImpactPoliticalHtmlLayouts,
          BULK_IMAGE: ctrl.highImpactPoliticalImageLayouts,
          BULK_VIDEO: ctrl.highImpactPoliticalVideoLayouts,
          BULK_THIRD_PARTY: ctrl.highImpactPoliticalThirdPartyLayouts
        };

        ctrl.validTypes = {};
        ctrl.validSubTypes = {};
        ctrl.subTypes = ['allowedStandard', 'allowedPolitical', 'allowedHighImpact', 'allowedHighImpactPolitical'];
        _.forEach(['BULK_HTML', 'BULK_IMAGE', 'BULK_VIDEO', 'BULK_THIRD_PARTY'], function(type) {
          ctrl.validSubTypes[type] = {};
          _.forEach(ctrl.subTypes, function(subType) {
            ctrl.validTypes[type] = ctrl.validTypes[type] || _.get(ctrl[subType][type], ['length']) > 0;
            ctrl.validSubTypes[type][subType] = ctrl.validSubTypes[type][subType] || _.get(ctrl[subType][type], ['length']) > 0;
            ctrl.validSubTypes[type]['asArray'] = ctrl.validSubTypes[type]['asArray'] || [];
            if (ctrl.validSubTypes[type][subType] && ctrl.validSubTypes[type]['asArray'].indexOf(subType) < 0) {
              ctrl.validSubTypes[type]['asArray'].push(subType);
            }
          });
        });
        ctrl.typeSwitch();

      }).finally(function() {
        // Spinner [resolved] false→true must not flip mid-CD (NG0100).
        deferMarkForCheck(cdr, function() {
          ctrl.layoutQueryDone = true;
        });
      });

      let bulkForm;
      ctrl.setForm = function(form) {
        bulkForm = form;
      };

      ctrl.copyInLayoutBulk = function(creative) {
        const currentLayout = _.cloneDeep(creative.layout);
        _.forEach(ctrl.model.creatives, function(c) {
          c.layout = currentLayout || null;
          ctrl.layoutChange(c);
        });
      };
      ctrl.copyInLineItemBulk = function(creative) {
        const currentLineItems = _.cloneDeep(creative.lineItems);
        const currentOrder = _.cloneDeep(creative.order);
        _.forEach(ctrl.model.creatives, function(c) {
          c.lineItemAssignmentType = ctrl.lineItemOption ? 'LINE_ITEM' : ctrl.defaultAssignmentType;
          c.lineItems = _.cloneDeep(currentLineItems || null);
          c.order = _.cloneDeep(currentOrder || null);
        });
      };
      ctrl.copyInCreativeSetBulk = function(creative) {
        const currentCreativeSets = _.cloneDeep(creative.creativeSets);
        _.forEach(ctrl.model.creatives, function(c) {
          c.lineItemAssignmentType = 'CREATIVE_SET';
          c.creativeSets = _.cloneDeep(currentCreativeSets || null);
        });
      };
      ctrl.copyInOrderBulk = function(creative) {
        const currentOrder = _.cloneDeep(creative.order);
        _.forEach(ctrl.model.creatives, function(c) {
          c.lineItemAssignmentType = 'ORDER';
          c.order = _.cloneDeep(currentOrder || null);
        });
      };
      ctrl.copyInTeamsBulk = function(creative) {
        const currentTeams = _.cloneDeep(creative.teams);
        _.forEach(ctrl.model.creatives, function(c) {
          c.teams = currentTeams || null;
        });
      };
      ctrl.toggleHalfSize = function(creative) {
        applyHalfSizeDimensions(creative, !!creative.halfSize);
      };
      ctrl.copyHalfSizeSettingBulk = function(creative) {
        copyHalfSizeSetting(creative, ctrl.model.creatives);
      };
      ctrl.copyUrl = function(tag, url) {
        _.forEach(ctrl.model.creatives, function(c) {
          c.bulkLayoutComponents = c.bulkLayoutComponents || {};
          const match = _.find(c.bulkLayoutComponents, function(blc) {
            return blc.tag === tag;
          });
          if (match) {
            match.value = url;
          }
        });
      };

      function updateLayout(creative, layout) {
        const prevComps = _.cloneDeep(creative.bulkLayoutComponents);
        creative.bulkLayoutComponents = [];
        _.forEach(layout.layoutComponents, function(lc) {
          const matchingTag = _.find(prevComps, function(comp) {
            return comp.tag === lc.tag;
          });
          creative.bulkLayoutComponents.push({
            tag: lc.tag,
            type: lc.type,
            textType: lc.textType,
            name: lc.name || lc.tag,
            required: lc.required,
            mimeTypes: lc.mimeTypes,
            defaultValue: lc.defaultValue,
            display: lc.display,
            options: lc.options,
            value: _.get(matchingTag, ['value'], _.get(lc, ['defaultValue'], ''))
          });
        });
      }

      function addNewCreativeSet(creative) {
        const newId = Uuid.generate();
        creative.creativeSets[newId] = {uiId: newId};
      }

      ctrl.removeCreativeSet = function(creative, index) {
        delete creative.creativeSets[index];
        if (_.isEmpty(creative.creativeSets)) {
          addNewCreativeSet(creative);
        }
      };

      ctrl.addCreativeSet = function(index) {
        addNewCreativeSet(ctrl.model.creatives[index]);
      };

      function addNewLineItem(creative) {
        const newId = Uuid.generate();
        creative.lineItems[newId] = {uiId: newId};
      }

      ctrl.setLineItemAssignmentType = function(creative, type) {
        creative.lineItemAssignmentType = type;
        if (type === 'ORDER') {
          const newId = Uuid.generate();
          creative.lineItems = {};
          creative.lineItems[newId] = {uiId: newId};
        } else if (type === 'CREATIVE_SET') {
          if (_.isEmpty(creative.creativeSets)) {
            creative.creativeSets = {};
            addNewCreativeSet(creative);
          }
        } else if (_.isEmpty(creative.lineItems)) {
          creative.lineItems = {};
          addNewLineItem(creative);
        }
      };

      ctrl.removeLineItem = function(creative, index) {
        delete creative.lineItems[index];
        if (_.isEmpty(creative.lineItems)) {
          addNewLineItem(creative);
        }
      };

      ctrl.addLineItem = function(index) {
        addNewLineItem(ctrl.model.creatives[index]);
      };

      ctrl.layoutChange = function(creative) {
        const compWithAsset = _.find(creative.bulkLayoutComponents, function(blc) {
          return blc.type === 'ASSET' && blc.mimeTypes && _.get(MediaTypeHelper.getMediaType(blc.mimeTypes), [ctrl.type === 'BULK_IMAGE' ? 'image' : 'video']) && blc.value;
        });
        updateLayout(creative, creative.layout);
        const newComp = _.find(creative.bulkLayoutComponents, function(blc) {
          return blc.type === 'ASSET' && blc.mimeTypes && _.get(MediaTypeHelper.getMediaType(blc.mimeTypes), [ctrl.type === 'BULK_IMAGE' ? 'image' : 'video']);
        });
        if (newComp && compWithAsset) {
          newComp.value = compWithAsset.value;
        }
      };

      ctrl.selectTeams = function(creative) {
        creative.teams = _.map(ctrl.allTeams, 'id');
      };

      ctrl.deselectTeams = function(creative) {
        creative.teams = [];
      };

      ctrl.bulkSubmitPending = false;
      ctrl.bulkSubmit = function() {
        if (ctrl.bulkSubmitPending) {
          return ctrl.bulkSubmitPromise;
        }
        ctrl.bulkSubmitPending = true;

        const bulkHtmlAssets = [];
        const activeCreatives = _.filter(ctrl.model.creatives, function(c) {
          return !c.skip;
        });
        const orderIds = _.uniq(_.map(_.filter(activeCreatives, function(c) {
          return c.lineItemAssignmentType === 'ORDER' && _.get(c, ['order', 'id']);
        }), function(c) {
          return c.order.id;
        }));

        ctrl.hasError = false;
        ctrl.bulkOrderErrors = [];
        ctrl.submitState.validSubmission = false;
        ctrl.submitState.apiErrors = [];
        ctrl.submitState.bulkError = false;
        setFormValidity(bulkForm, 'validation', true);

        const orderLookupPromise = Promise.all(_.map(orderIds, function(orderId) {
          return LineItem.query({order: orderId, pageSize: 1000}).$promise.then(function(page) {
            return {
              orderId: orderId,
              lineItems: _.uniqBy(_.filter(page.results, function(li) {
                return li.id;
              }), 'id')
            };
          });
        })).then(function(results) {
          return _.keyBy(results, 'orderId');
        });

        ctrl.bulkSubmitPromise = orderLookupPromise.then(function(orderLineItemsLookup) {
          const uiCreatives = _.map(activeCreatives, function(c) {
            const creative: any = {
              id: c.id || Uuid.generate(),
              name: c.name || 'Bulk Creative',
              lineItems: c.lineItemAssignmentType === 'ORDER' ? _.keyBy(_.get(orderLineItemsLookup, [c.order.id, 'lineItems'], []), 'id') : c.lineItems,
              creativeSets: c.creativeSets,
              width: c.width,
              height: c.height,
              layout: c.layout,
              layoutParameters: {},
              teams: c.teams
            };
            if (c.impTrackingUrl && c.impTrackingUrl.length > 1) {
              creative.impressionTrackingUrls = [c.impTrackingUrl];
            }
            _.forEach(c.bulkLayoutComponents, function(blc) {
              creative.layoutParameters[blc.tag] = blc.value;

              if (_.get(blc, ['value', 'primaryHtmlAsset']) && !_.isEmpty(_.get(blc, ['value', 'htmlUrls']))) {
                bulkHtmlAssets.push({id: blc.value.id, htmlUrls: blc.value.htmlUrls});
              }
            });
            return creative;
          });
          _.forEach(activeCreatives, function(c) {
            if (c.lineItemAssignmentType === 'ORDER' && !_.get(c, ['order', 'id'])) {
              ctrl.bulkOrderErrors.push({
                creativeName: c.name || 'Bulk Creative',
                message: 'No order selected'
              });
              return;
            }
            if (c.lineItemAssignmentType === 'ORDER' && _.get(c, ['order', 'id']) && _.get(orderLineItemsLookup, [c.order.id, 'lineItems', 'length']) === 0) {
              ctrl.bulkOrderErrors.push({
                creativeName: c.name || 'Bulk Creative',
                message: (_.get(c, ['order', 'name']) || 'Selected order') + ' has no line items'
              });
            }
          });

          if (ctrl.bulkOrderErrors.length > 0) {
            ctrl.hasError = true;
            setFormValidity(bulkForm, 'validation', false);
            return Promise.reject();
          }

          if (bulkHtmlAssets.length > 0) {
            return Asset.bulkSave(null, bulkHtmlAssets).then(function() {
              return saveCreatives();
            });
          }
          return saveCreatives();

          function saveCreatives() {
            const {bulkSaveCreatives, copyCreatives, allCreatives} = buildCreativeBulkSavePlan(uiCreatives, ctrl.advertiser);
            return CreativeResource.bulkSave(bulkSaveCreatives).$promise.then(function() {
              const promises = [];
              _.forEach(copyCreatives, function(cc) {
                promises.push(CreativeResource.copyCreative(cc.copyCreativeId, cc.liId, cc.id, null, cc.csId));
              });
              return Promise.all(promises).then(function() {
                if (creativeType === 'CREATIVE') {
                  LocalBulkCreativeInfo.obtain().setCreatives(allCreatives);
                  ctrl.bulkCreatives = LocalBulkCreativeInfo.obtain().getCreatives();
                } else {
                  LocalBulkCreativeInfo.obtain().setLibraryCreatives(allCreatives);
                  ctrl.bulkCreatives = LocalBulkCreativeInfo.obtain().getLibraryCreatives();
                }
                adnListHelper.setUpList(ctrl, CreativeResource, false, null, {id: _.map(ctrl.bulkCreatives, 'id'), pageSize: 1000});
                deferMarkForCheck(cdr, function() {
                  ctrl.hasUpload = false;
                  ctrl.submitState.validSubmission = true;
                  ctrl.model.creatives = [];
                });
              });
            });
          }
        }).catch(function(response) {
          if (!_.isNil(response)) {
            applyCreativeBulkUploadValidationErrors(ctrl.submitState, response);
          }
          deferMarkForCheck(cdr, function() {
            ctrl.hasError = true;
          });
        }).finally(function() {
          bulkForm.control.markAsPristine();
          deferMarkForCheck(cdr, function() {
            ctrl.bulkSubmitPending = false;
            ctrl.bulkSubmitPromise = null;
          });
        });
        return ctrl.bulkSubmitPromise;
      };

      ctrl.refreshCodeMirror = function() {
        ctrl.refreshCodeMirrorVar = false;
        setTimeout(function() {
          ctrl.refreshCodeMirrorVar = true;
        }, 200);
      };
      ctrl.refreshCodeMirror();

      ctrl.incorrectMimeTypeFiles = null;
      ctrl.filesFailedUpload = null;
      ctrl.cannotProcess = [];
      ctrl.eventHook = {
        onUpdate: function(asset, allFilesProcessed, uploadStats) {
          ctrl.isNew = false;

          const incorrectMimeTypeFiles = ctrl.incorrectMimeTypeFiles
            || _.cloneDeep(uploadStats.incorrectMimeType);
          const filesFailedUpload = ctrl.filesFailedUpload
            || _.cloneDeep(uploadStats.filesFailedUpload);

          const totalFilesCount = uploadStats.apiResponseCount + uploadStats.incorrectMimeType.length + uploadStats.filesFailedUpload.length;
          const loadingCreatives = totalFilesCount !== uploadStats.allFilesCount && totalFilesCount !== uploadStats.fileUploadLimit;

          const selectableLayouts = ctrl[ctrl.selected][ctrl.type];
          const bulkLayout = _.get(selectableLayouts, [0]);

          const unprocessedAssets = _.flatten(_.map(asset, function(a) {
            return a.results || a;
          }));
          const allAssets = _.filter(unprocessedAssets, function(a) {
            return !a.zipCdnId || a.primaryHtmlAsset;
          });
          const uiId = Uuid.generate();
          const csUiId = Uuid.generate();
          const currentCreatives = _.flatten(_.map(allAssets, function(a) {
            const assetName = a.primaryHtmlAsset ? a.zipName : a.fileName;
            const creative: any = {
              name: assetName || 'Creative Name',
              width: a.width,
              height: a.height,
              nativeWidth: a.width,
              nativeHeight: a.height,
              halfSize: false,
              lineItemAssignmentType: ctrl.defaultAssignmentType,
              lineItems: {},
              creativeSets: {},
              skip: false,
              order: {},
              layout: bulkLayout
            };
            const liRef: any = {uiId: uiId};
            if (ctrl.lineItem && ctrl.lineItem.id) {
              liRef.id = ctrl.lineItem.id;
              liRef.name = ctrl.lineItem.name;
            }
            creative.lineItems[uiId] = liRef;
            const csRef: any = {csUiId: csUiId};
            if (ctrl.creativeSetFromParams && ctrl.creativeSetFromParams.id) {
              csRef.id = ctrl.creativeSetFromParams.id;
              csRef.name = ctrl.creativeSetFromParams.name;
            }
            creative.creativeSets[csUiId] = csRef;
            if (ctrl.type === 'BULK_IMAGE') {
              updateLayout(creative, bulkLayout);
              const comp = _.find(creative.bulkLayoutComponents, function(blc) {
                return blc.type === 'ASSET' && blc.mimeTypes && _.get(MediaTypeHelper.getMediaType(blc.mimeTypes), ['image']);
              });
              comp.value = a;
              return creative;
            }
            if (ctrl.type === 'BULK_VIDEO') {
              updateLayout(creative, bulkLayout);
              const comp = _.find(creative.bulkLayoutComponents, function(blc) {
                return blc.type === 'ASSET' && blc.mimeTypes && _.get(MediaTypeHelper.getMediaType(blc.mimeTypes), ['video']);
              });
              comp.value = a;
              return creative;
            }
            if (ctrl.type === 'BULK_HTML') {
              updateLayout(creative, bulkLayout);
              const comp = _.find(creative.bulkLayoutComponents, function(blc) {
                return blc.type === 'ASSET' && blc.mimeTypes && _.get(MediaTypeHelper.getMediaType(blc.mimeTypes), ['html']);
              });
              comp.value = a;
              return creative;
            }
            return _.map(a.creatives, function(c) {
              c.name = c.creativeName || 'Creative Name';
              c.layout = bulkLayout;
              c.lineItemAssignmentType = ctrl.defaultAssignmentType;
              c.lineItems = {};
              const lineItemRef: any = {uiId: uiId};
              if (ctrl.lineItem && ctrl.lineItem.id) {
                lineItemRef.id = ctrl.lineItem.id;
                lineItemRef.name = ctrl.lineItem.name;
              }
              c.lineItems[uiId] = lineItemRef;
              c.order = {};
              c.creativeSets = {};
              c.creativeSets[csUiId] = {csUiId: csUiId};
              c.skip = false;
              updateLayout(c, bulkLayout);
              const comp = _.find(c.bulkLayoutComponents, function(blc) {
                return blc.tag === 'thirdParty';
              });
              comp.value = c.tag;
              return c;
            });
          }));
          const cannotProcessZip = (!currentCreatives || !currentCreatives.length)
            && unprocessedAssets.length > 0
            && unprocessedAssets[0].zipCdnId
            ? unprocessedAssets[0]
            : null;

          // Upload settle runs inside ngZone during an existing CD turn; swapping
          // model.creatives (ngFor) / hasUpload mid-check is NG0100.
          deferMarkForCheck(cdr, function() {
            ctrl.incorrectMimeTypeFiles = incorrectMimeTypeFiles;
            ctrl.filesFailedUpload = filesFailedUpload;
            ctrl.loadingCreatives = loadingCreatives;
            ctrl.selectableLayouts = selectableLayouts;
            ctrl.bulk.layout = bulkLayout;
            ctrl.model.creatives = ctrl.model.creatives || [];
            if (currentCreatives.length) {
              ctrl.model.creatives = ctrl.model.creatives.concat(currentCreatives);
            }
            ctrl.hasUpload = ctrl.model.creatives.length > 0;
            ctrl.noValidAssets = !ctrl.loadingCreatives && !ctrl.hasUpload;
            if (cannotProcessZip) {
              ctrl.cannotProcess.push(cannotProcessZip);
            }
            ctrl.refreshCodeMirror();
          });
        }
      };

}
